import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { SBHorizontal } from 'atoms/layout/flex';
import { DotsLoader } from 'atoms/loader/DotsLoader';
import { BodyM } from 'atoms/typography';
import { Bold } from 'atoms/typography/Bold';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useEventsContext } from 'contexts/events';
import { useIntlContext } from 'contexts/intl';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { useCurrentUserCurrencies } from 'hooks/useCurrentUserCurrencies';
import { useInviteLink } from 'hooks/useInviteLink';
import { LIFECYCLE, useLifecycle } from 'hooks/useLifecycle';
import { useReferralProgramConfig } from 'hooks/useReferralProgramConfig';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import { TermsDialog } from '../../conversionCredit/TermsDialog';
import { DiscountBannerWrapper } from '../DiscountBannerWrapper';

const Buttons = styled(SBHorizontal)`
  width: 100%;
  @media ${tabletAndAbove} {
    width: auto;
  }
`;
const TermsLink = styled(ButtonBase)`
  color: var(--c-white);
  text-decoration: underline;
`;

type Props = {
  sport: Sport;
};
export const ReferralBanner = ({ sport }: Props) => {
  const { userFiatAmount: referralProgramConfigFiatAmount } =
    useReferralProgramConfig();
  const { currentUser } = useCurrentUserContext();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { up: isTabletOrDesktop } = useScreenSize('tablet');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { formatNumber } = useIntlContext();
  const { track } = useEventsContext();
  const inviteLink = useInviteLink(sport);
  const { update, lifecycle } = useLifecycle();

  const hideReferralBanner = lifecycle?.hideReferralBanner;

  if (!currentUser || hideReferralBanner) return null;

  const { referralRewardsCount } = currentUser;

  if (referralRewardsCount > 0) return null;

  const amount =
    referralProgramConfigFiatAmount &&
    formatNumber(referralProgramConfigFiatAmount, {
      style: 'currency',
      currency: fiatCurrency,
      maximumFractionDigits: 0,
    });

  return (
    <DiscountBannerWrapper
      rightNode={
        <Buttons gap={1.5}>
          <Button
            size="small"
            color="transparent"
            fullWidth={!isTabletOrDesktop}
            onClick={() => {
              update(LIFECYCLE.hideReferralBanner, true);
            }}
          >
            <FormattedMessage
              id="ReferralBanner.dismiss"
              defaultMessage="Dismiss"
            />
          </Button>
          <Button
            size="small"
            color="secondary"
            fullWidth={!isTabletOrDesktop}
            to={inviteLink}
            onClick={() => track('Click Referral Banner')}
          >
            <FormattedMessage
              id="ReferralBanner.referAFriend"
              defaultMessage="Refer a friend"
            />
          </Button>
        </Buttons>
      }
    >
      {referralProgramConfigFiatAmount === undefined ? (
        <DotsLoader />
      ) : (
        <BodyM as="span" color="var(--c-white)">
          <FormattedMessage
            id="ReferralBanner.getDiscountReward2"
            defaultMessage="Get up to <b>{amount}</b> credit for each referred friend"
            values={{
              b: Bold,
              amount,
            }}
          />{' '}
          <TermsLink type="button" onClick={() => setOpenDialog(true)}>
            <FormattedMessage {...glossary.termsApply} />
          </TermsLink>
        </BodyM>
      )}
      {!!openDialog && <TermsDialog onClose={() => setOpenDialog(false)} />}
    </DiscountBannerWrapper>
  );
};
